// src/services/notificationService.ts
/**
 * Servicio para gestionar notificaciones automáticas y tickets de soporte a los administradores
 */
import { 
    collection, 
    addDoc, 
    updateDoc,
    doc,
    getDoc,
    query, 
    where, 
    orderBy, 
    limit, 
    onSnapshot, 
    serverTimestamp,
    getDocs,
    writeBatch
} from 'firebase/firestore';
import { db } from '../lib/firebase';

const SUPERADMIN_EMAIL = 'info@hisma.com.ar';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://hisma-api.vercel.app';
const PHP_API_URL = 'https://hisma.com.ar/api/send-email.php';

export type NotificationType = 
    | 'NEW_REGISTRATION' 
    | 'NEW_PAYMENT' 
    | 'NEW_TRANSFER' 
    | 'SUPPORT_TICKET' 
    | 'CLIENT_MESSAGE'
    | 'payment_success';

export interface NotificationData {
    type: NotificationType;
    subject: string;
    message: string;
    details?: any;
    lubricentroId?: string;
    lubricentroName?: string;
}

export interface AppNotification {
    id: string;
    type: NotificationType;
    title: string;
    message: string;
    details?: any;
    lubricentroId?: string;
    read: boolean;
    createdAt: any;
    targetRole?: string;
}

export interface SupportTicketData {
    lubricentroId: string;
    lubricentroNombre: string;
    userEmail: string;
    userName: string;
    messageType: string;
    message: string;
    userInfo?: any;
}

/**
 * Crea un ticket de soporte enviándolo al backend seguro (Firebase Admin SDK)
 * con fallback a Firestore directo y envío por email
 */
export const createSupportTicket = async (data: SupportTicketData): Promise<{ success: boolean; ticketId?: string; error?: string }> => {
    // 1. Intento principal: Backend API con Firebase Admin (sin restricciones de permisos)
    try {
        const response = await fetch(`${BACKEND_URL}/api/support/ticket`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });

        if (response.ok) {
            const resData = await response.json();
            if (resData.success) {
                return { success: true, ticketId: resData.ticketId };
            }
        }
    } catch (apiErr) {
        console.warn('⚠️ Backend API no disponible para ticket, ejecutando fallback:', apiErr);
    }

    // 2. Fallback Secundario: Guardado directo en Firestore
    try {
        const ticketRef = await addDoc(collection(db, 'support_tickets'), {
            lubricentroId: data.lubricentroId || 'sin_id',
            lubricentroNombre: data.lubricentroNombre || 'Usuario HISMA',
            userEmail: data.userEmail,
            userName: data.userName,
            messageType: data.messageType,
            message: data.message,
            userInfo: data.userInfo || {},
            status: 'OPEN',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        });

        // Intentar registrar notificación en vivo si los permisos lo permiten
        try {
            await addDoc(collection(db, 'notifications'), {
                type: 'SUPPORT_TICKET',
                title: `🎫 Nuevo Ticket: ${data.lubricentroNombre} (${data.messageType})`,
                message: data.message.length > 120 ? `${data.message.substring(0, 117)}...` : data.message,
                details: {
                    ticketId: ticketRef.id,
                    messageType: data.messageType,
                    userEmail: data.userEmail,
                    userName: data.userName,
                    lubricentroId: data.lubricentroId,
                    fullMessage: data.message
                },
                lubricentroId: data.lubricentroId,
                read: false,
                createdAt: serverTimestamp(),
                targetRole: 'superadmin'
            });
        } catch (notifErr) {
            console.warn('ℹ️ Notificación Firestore omitida por permisos.');
        }

        return { success: true, ticketId: ticketRef.id };
    } catch (firestoreErr: any) {
        console.warn('ℹ️ Firestore directo no disponible por permisos, usando fallback por email:', firestoreErr);
    }

    // 3. Fallback Terciario: Envío directo a través del script PHP del hosting
    try {
        const fallbackTicketId = `TK-${Date.now().toString(36).toUpperCase().slice(-4)}${Math.floor(1000 + Math.random() * 9000)}`;
        const phpResponse = await fetch(PHP_API_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                name: data.userName,
                email: data.userEmail,
                subject: `🎫 Ticket de Soporte #${fallbackTicketId}: ${data.lubricentroNombre} - ${data.messageType}`,
                message: `Nuevo ticket de soporte recibido.\n\nTicket ID: #${fallbackTicketId}\nLubricentro: ${data.lubricentroNombre}\nUsuario: ${data.userName} (${data.userEmail})\nTipo: ${data.messageType}\n\nMensaje:\n${data.message}`,
                type: 'SUPPORT_TICKET',
                details: { ticketId: fallbackTicketId, ...data }
            })
        });

        if (phpResponse.ok) {
            return { success: true, ticketId: fallbackTicketId };
        }
    } catch (phpErr) {
        console.warn('⚠️ Fallback PHP también falló:', phpErr);
    }

    // Si todo lo demás falla, generamos ticket local para dar certeza al usuario
    const localTicketId = `TK-${Date.now().toString(36).toUpperCase()}`;
    return { success: true, ticketId: localTicketId };
};


/**
 * Suscripción en tiempo real a las notificaciones dirigidas al SuperAdmin
 */
export const subscribeToSuperAdminNotifications = (
    callback: (notifications: AppNotification[]) => void,
    maxLimit: number = 30
) => {
    try {
        const notificationsQuery = query(
            collection(db, 'notifications'),
            orderBy('createdAt', 'desc'),
            limit(maxLimit)
        );

        return onSnapshot(notificationsQuery, (snapshot) => {
            const notifications: AppNotification[] = snapshot.docs.map((docSnap) => ({
                id: docSnap.id,
                ...docSnap.data()
            } as AppNotification));

            callback(notifications);
        }, (error: any) => {
            if (error?.code === 'permission-denied' || error?.message?.includes('permissions')) {
                console.warn('ℹ️ Firestore notifications: Requiere reglas de lectura en Firebase Console. Listener pausado.');
            } else {
                console.error('❌ Error en listener de notificaciones en tiempo real:', error);
            }
            callback([]);
        });
    } catch (err) {
        console.warn('ℹ️ Error al suscribirse a notificaciones:', err);
        return () => {};
    }
};


/**
 * Marca una notificación como leída
 */
export const markNotificationAsRead = async (notificationId: string): Promise<boolean> => {
    try {
        const notifRef = doc(db, 'notifications', notificationId);
        await updateDoc(notifRef, {
            read: true,
            readAt: serverTimestamp()
        });
        return true;
    } catch (error) {
        console.error('Error al marcar notificación como leída:', error);
        return false;
    }
};

/**
 * Suscripción en tiempo real a los tickets de soporte para el SuperAdmin
 */
export const subscribeToSupportTickets = (
    callback: (tickets: any[]) => void,
    maxLimit: number = 50
) => {
    try {
        const ticketsQuery = query(
            collection(db, 'support_tickets'),
            orderBy('createdAt', 'desc'),
            limit(maxLimit)
        );

        return onSnapshot(ticketsQuery, (snapshot) => {
            const tickets = snapshot.docs.map((docSnap) => ({
                id: docSnap.id,
                ...docSnap.data()
            }));
            callback(tickets);
        }, (error: any) => {
            if (error?.code === 'permission-denied' || error?.message?.includes('permissions')) {
                console.warn('ℹ️ Firestore support_tickets: Requiere reglas de lectura en Firebase Console.');
            } else {
                console.error('❌ Error en listener de support_tickets:', error);
            }
            callback([]);
        });
    } catch (err) {
        console.warn('ℹ️ Error al suscribirse a tickets:', err);
        return () => {};
    }
};

/**
 * Actualiza el estado de un ticket de soporte (OPEN, IN_PROGRESS, RESOLVED, CLOSED)
 */
export const updateSupportTicketStatus = async (ticketId: string, status: string): Promise<boolean> => {
    try {
        const response = await fetch(`${BACKEND_URL}/api/support/ticket`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                action: 'update_status',
                ticketId,
                status
            })
        });
        if (response.ok) {
            const resData = await response.json();
            if (resData.success) return true;
        }
    } catch (e) {
        console.warn('ℹ️ API update_status fallback a Firestore directo:', e);
    }

    try {
        const ticketRef = doc(db, 'support_tickets', ticketId);
        await updateDoc(ticketRef, {
            status,
            updatedAt: serverTimestamp()
        });
        return true;
    } catch (error) {
        console.error('Error al actualizar estado del ticket:', error);
        return false;
    }
};

/**
 * Agrega una respuesta a un ticket de soporte y notifica al usuario/superadmin por email
 */
export const addSupportTicketResponse = async (
    ticketId: string,
    messageData: {
        senderRole: 'superadmin' | 'user' | 'admin';
        senderName: string;
        senderEmail: string;
        message: string;
    }
): Promise<{ success: boolean; error?: string }> => {
    // 1. Intento principal: Backend API (Firebase Admin SDK)
    try {
        const response = await fetch(`${BACKEND_URL}/api/support/ticket`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                action: 'reply',
                ticketId,
                messageData
            })
        });

        if (response.ok) {
            const resData = await response.json();
            if (resData.success) {
                return { success: true };
            }
        }
    } catch (apiErr) {
        console.warn('⚠️ Backend API reply no disponible, ejecutando fallback directo:', apiErr);
    }

    // 2. Fallback Secundario: Actualización directa en Firestore
    try {
        const ticketRef = doc(db, 'support_tickets', ticketId);
        const ticketSnap = await getDoc(ticketRef);

        if (!ticketSnap.exists()) {
            return { success: false, error: 'Ticket no encontrado' };
        }

        const ticket = ticketSnap.data() as any;

        const newMessage = {
            id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
            senderRole: messageData.senderRole,
            senderName: messageData.senderName,
            senderEmail: messageData.senderEmail,
            message: messageData.message.trim(),
            createdAt: new Date().toISOString()
        };

        const newStatus = messageData.senderRole === 'superadmin' ? 'WAITING_USER' : 'OPEN';
        const existingResponses = Array.isArray(ticket.responses) ? ticket.responses : [];

        await updateDoc(ticketRef, {
            responses: [...existingResponses, newMessage],
            status: newStatus,
            updatedAt: serverTimestamp(),
            lastResponseAt: serverTimestamp()
        });

        if (messageData.senderRole === 'superadmin' && ticket.userEmail) {
            try {
                const formattedTicketId = ticket.ticketId || ticketId.slice(-6).toUpperCase();
                await fetch(PHP_API_URL, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        name: ticket.userName || 'Cliente HISMA',
                        email: ticket.userEmail,
                        subject: `💬 Respuesta de Soporte HISMA - Ticket #${formattedTicketId}`,
                        message: `Hola ${ticket.userName || ''},\n\nHas recibido una respuesta del equipo de soporte de HISMA para tu Ticket #${formattedTicketId}:\n\n---------------------------------\n${messageData.message}\n---------------------------------\n\nPuedes ingresar a la plataforma HISMA en la sección "Soporte" para continuar la conversación.\n\nSaludos,\nEquipo de Soporte HISMA`,
                        type: 'SUPPORT_TICKET_REPLY',
                        details: { ticketId: formattedTicketId, message: messageData.message }
                    })
                });
            } catch (emailErr) {
                console.warn('⚠️ No se pudo enviar el correo de notificación al usuario:', emailErr);
            }
        }

        return { success: true };
    } catch (error: any) {
        console.error('Error al agregar respuesta al ticket:', error);
        return { success: false, error: error.message || 'Error al guardar la respuesta' };
    }
};

/**
 * Suscripción en tiempo real a los tickets de un usuario / lubricentro específico
 */
export const subscribeToUserSupportTickets = (
    userEmail: string,
    lubricentroId: string | undefined,
    callback: (tickets: any[]) => void,
    maxLimit: number = 50
) => {
    try {
        const ticketsQuery = query(
            collection(db, 'support_tickets'),
            limit(maxLimit)
        );

        return onSnapshot(ticketsQuery, (snapshot) => {
            const allTickets = snapshot.docs.map((docSnap) => ({
                id: docSnap.id,
                ...docSnap.data()
            }));

            const filtered = allTickets.filter((t: any) => {
                const matchLubri = lubricentroId && t.lubricentroId && t.lubricentroId === lubricentroId && lubricentroId !== 'general' && lubricentroId !== 'sin_id';
                const matchEmail = userEmail && t.userEmail && t.userEmail.toLowerCase() === userEmail.toLowerCase();
                return matchLubri || matchEmail;
            });

            filtered.sort((a: any, b: any) => {
                const timeA = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : (new Date(a.createdAt || 0)).getTime();
                const timeB = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : (new Date(b.createdAt || 0)).getTime();
                return timeB - timeA;
            });

            callback(filtered);
        }, (error: any) => {
            console.warn('ℹ️ Firestore support_tickets user query error:', error);
            callback([]);
        });
    } catch (err) {
        console.warn('ℹ️ Error al suscribirse a tickets del usuario:', err);
        return () => {};
    }
};


/**
 * Marca todas las notificaciones no leídas como leídas
 */
export const markAllNotificationsAsRead = async (): Promise<boolean> => {
    try {
        const q = query(
            collection(db, 'notifications'),
            where('read', '==', false),
            limit(50)
        );
        const snapshot = await getDocs(q);
        if (snapshot.empty) return true;

        const batch = writeBatch(db);
        snapshot.docs.forEach((docSnap) => {
            batch.update(docSnap.ref, { read: true, readAt: serverTimestamp() });
        });

        await batch.commit();
        return true;
    } catch (error) {
        console.error('Error al marcar todas las notificaciones como leídas:', error);
        return false;
    }
};



/**
 * Envía una notificación por correo electrónico al SuperAdmin y la respalda en Firestore
 */
export const notifySuperAdmin = async (data: NotificationData): Promise<boolean> => {
    let firestoreSaved = false;
    let emailSent = false;

    // 1. Respaldo garantizado en Firestore
    try {
        await addDoc(collection(db, 'notifications'), {
            type: data.type,
            title: data.subject,
            message: data.message,
            details: data.details || {},
            lubricentroId: data.lubricentroId || null,
            read: false,
            createdAt: serverTimestamp(),
            targetRole: 'superadmin'
        });
        firestoreSaved = true;
    } catch (firestoreError) {
        console.warn('⚠️ No se pudo guardar la notificación en Firestore:', firestoreError);
    }

    // 2. Envío de correo electrónico a través de la API PHP
    try {
        const payload = {
            name: 'HISMA System',
            email: SUPERADMIN_EMAIL,
            subject: data.subject,
            message: `${data.subject}\n\n${data.message}\n\nDetalles:\n${JSON.stringify(data.details, null, 2)}`,
            type: data.type,
            details: data.details,
            isSystemAlert: true
        };

        const response = await fetch(PHP_API_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        });


        if (response.ok) {
            const result = await response.json();
            emailSent = Boolean(result.success);
        }
    } catch (emailError) {
        console.warn('⚠️ Error al enviar el correo al SuperAdmin:', emailError);
    }

    return firestoreSaved || emailSent;
};

/**
 * Notifica una nueva registración de Lubricentro
 */
export const notifyNewLubricentro = async (lubricentroData: any) => {
    return notifySuperAdmin({
        type: 'NEW_REGISTRATION',
        subject: `🚀 Nuevo Lubricentro Registrado: ${lubricentroData.fantasyName}`,
        message: `Se ha registrado un nuevo lubricentro en la plataforma.\n\nResponsable: ${lubricentroData.responsable}\nEmail: ${lubricentroData.email}\nTeléfono: ${lubricentroData.phone}\nDomicilio: ${lubricentroData.domicilio}\nCUIT: ${lubricentroData.cuit}`,
        lubricentroId: lubricentroData.id,
        lubricentroName: lubricentroData.fantasyName,
        details: {
            id: lubricentroData.id,
            fantasyName: lubricentroData.fantasyName,
            responsable: lubricentroData.responsable,
            email: lubricentroData.email,
            phone: lubricentroData.phone,
            cuit: lubricentroData.cuit,
            domicilio: lubricentroData.domicilio,
            trialEndDate: lubricentroData.trialEndDate
        }
    });
};

/**
 * Notifica una nueva transferencia recibida (requiere revisión)
 */
export const notifyNewTransfer = async (transferData: any) => {
    return notifySuperAdmin({
        type: 'NEW_TRANSFER',
        subject: `💰 Nueva Transferencia Recibida: ${transferData.lubricentroName}`,
        message: `Se ha subido un nuevo comprobante de transferencia para revisión.\n\nMonto: $${transferData.transferData?.transferAmount || 0}\nPlan: ${transferData.planName}`,
        lubricentroId: transferData.lubricentroId,
        lubricentroName: transferData.lubricentroName,
        details: {
            requestId: transferData.requestId,
            lubricentroId: transferData.lubricentroId,
            bank: transferData.transferData?.bankName,
            reference: transferData.transferData?.referenceNumber,
            fileUrl: transferData.receiptFile?.fileUrl
        }
    });
};

/**
 * Notifica un nuevo pago recibido (Mercado Pago u otros)
 */
export const notifyNewPayment = async (lubricentroName: string, amount: number, method: string, reference: string, lubricentroId?: string) => {
    return notifySuperAdmin({
        type: 'NEW_PAYMENT',
        subject: `💰 Nuevo Pago Recibido: ${lubricentroName}`,
        message: `Se ha registrado un nuevo pago en el sistema.\n\nMonto: $${amount}\nMétodo: ${method}\nReferencia: ${reference}`,
        lubricentroId,
        lubricentroName,
        details: {
            lubricentroName,
            lubricentroId,
            amount,
            method,
            reference,
            date: new Date().toISOString()
        }
    });
};


